import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { showErrorToast } from "../../components/CustomComponents/toast/toast";
import { defaultSelectPortalProps } from "../staff/dropdownStyles";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import "./dealPerformanceReport.css";

const toValidDate = (value) => {
  if (value == null) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return date instanceof Date && !isNaN(date.getTime()) ? date : null;
};

const PAGE_KEY = "dealPerformanceReport";
const DEFAULT_LOCATION = { value: "all", label: "All Locations" };
const DEFAULT_VIEW_MODE = { value: "packages", label: "By Package" };
const DEFAULT_PAGE_SIZE = { value: "20", label: "20" };
const VIEW_MODE_OPTIONS = [
  { value: "packages", label: "By Package" },
  { value: "details", label: "Order Lines" },
];
const VALID_PAGE_SIZES = ["10", "20", "50", "100"];

const DealPerformanceReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();

  const defaultDateRange = useMemo(
    () => [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
    []
  );

  const preferences = useSelector((state) => state.itemGridPreferences[PAGE_KEY] || {});
  const location = preferences.location || DEFAULT_LOCATION;
  const viewMode = preferences.viewMode || DEFAULT_VIEW_MODE;
  const dateRange = preferences.dateRange || defaultDateRange;
  const pageSizePref = preferences.pageSize || DEFAULT_PAGE_SIZE;
  const rawStartDate = dateRange?.[0] ?? null;
  const rawEndDate = dateRange?.[1] ?? null;
  const locationValue = location?.value ?? "all";
  const viewModeValue = viewMode?.value || "packages";

  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(isDarkMode, themeColor),
    [isDarkMode, themeColor]
  );

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code || "";

  const [locations, setLocations] = useState([DEFAULT_LOCATION]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [summary, setSummary] = useState(null);
  const [packages, setPackages] = useState([]);
  const [details, setDetails] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const searchTimeoutRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const summaryAbortRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const pageSizeValue = Number(pageSizePref?.value ?? pageSizePref ?? 20) || 20;

  const money = useCallback(
    (v) => `${getCurrencySymbol(compCurrency)} ${Number(v ?? 0).toFixed(2)}`,
    [compCurrency]
  );

  const buildFilterParams = useCallback(() => {
    const startDate = toValidDate(rawStartDate) || moment().startOf("week").toDate();
    const endDate = toValidDate(rawEndDate) || moment().endOf("week").toDate();
    const params = {
      CompID,
      startDate: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
    };
    if (locationValue && locationValue !== "all") {
      params.locationFilter = locationValue;
    }
    if (committedSearch) {
      params.searchTerm = committedSearch;
    }
    return params;
  }, [CompID, rawStartDate, rawEndDate, locationValue, committedSearch]);

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: filterName, value: selectedOption }));
  };

  const clearAllFilters = () => {
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "location", value: DEFAULT_LOCATION }));
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "viewMode", value: DEFAULT_VIEW_MODE }));
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
    setSearch("");
    setCommittedSearch("");
    setSummary(null);
    setPageNumber(1);
  };

  const hasFiltersApplied = () =>
    locationValue !== "all" || Boolean(committedSearch) || viewModeValue !== "packages";

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      let effectiveStartDate = newStartDate;
      let effectiveEndDate = newEndDate;
      if (newStartDate && newEndDate && newStartDate > newEndDate) {
        [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
      } else if (newStartDate && !newEndDate) {
        effectiveEndDate = new Date(newStartDate.getTime());
        effectiveEndDate.setHours(23, 59, 59, 999);
      }
      dispatch(
        setFilterField({
          pageKey: PAGE_KEY,
          field: "dateRange",
          value: [effectiveStartDate, effectiveEndDate],
        })
      );
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setCommittedSearch(search.trim()), 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search]);

  useEffect(() => {
    setSummary(null);
  }, [rawStartDate, rawEndDate, locationValue, viewModeValue, committedSearch]);

  useEffect(() => {
    if (
      !preferences.pageSize ||
      !VALID_PAGE_SIZES.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "pageSize", value: DEFAULT_PAGE_SIZE }));
    }
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "location", value: DEFAULT_LOCATION }));
    }
    if (!preferences.viewMode) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "viewMode", value: DEFAULT_VIEW_MODE }));
    }
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fetchLocations = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      setLocations([
        DEFAULT_LOCATION,
        ...arr.map((loc) => ({
          value: loc.CompID ?? loc.compID,
          label: String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${loc.CompID ?? ""}`,
        })),
      ]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([DEFAULT_LOCATION]);
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID]);

  const fetchReport = useCallback(async () => {
    if (!CompID) return;

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const fetchSeq = ++fetchSeqRef.current;

    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        includeDetails: viewModeValue === "details",
        pageNumber,
        pageSize: pageSizeValue,
      };

      const response = await axiosInstance.get(APIs.GET_DEAL_PERFORMANCE_REPORT, {
        params,
        signal: controller.signal,
      });
      if (controller.signal.aborted || fetchSeq !== fetchSeqRef.current) return;

      const ok = response.data?.status === true || response.data?.Status === true;
      if (ok) {
        const payload = response.data.data || response.data.Data || {};
        setPackages(payload.packages || payload.Packages || []);
        setDetails(payload.details || payload.Details || []);
        const total = Number(payload.totalCount ?? payload.TotalCount ?? 0);
        const pages = Number(payload.totalPages ?? payload.TotalPages ?? 0);
        setTotalRecords(total);
        setTotalPages(pages || (pageSizeValue > 0 ? Math.ceil(total / pageSizeValue) : 0));
      } else {
        setPackages([]);
        setDetails([]);
        setTotalRecords(0);
        setTotalPages(0);
        showErrorToast(
          response.data?.message ||
            response.data?.Message ||
            "Unable to fetch combo deal performance."
        );
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      if (fetchSeq !== fetchSeqRef.current) return;
      console.error(err);
      setPackages([]);
      setDetails([]);
      setTotalRecords(0);
      setTotalPages(0);
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err.message ||
          "Unable to fetch combo deal performance."
      );
    } finally {
      if (!controller.signal.aborted && fetchSeq === fetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, buildFilterParams, viewModeValue, pageNumber, pageSizeValue]);

  const fetchSummary = useCallback(async () => {
    if (!CompID) return;

    if (summaryAbortRef.current) {
      summaryAbortRef.current.abort();
    }
    const controller = new AbortController();
    summaryAbortRef.current = controller;

    setSummaryLoading(true);
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_PERFORMANCE_SUMMARY, {
        params: buildFilterParams(),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const ok = response.data?.status === true || response.data?.Status === true;
      if (ok) {
        setSummary(response.data.data || response.data.Data || null);
      } else {
        setSummary(null);
        showErrorToast(
          response.data?.message ||
            response.data?.Message ||
            "Unable to fetch combo deal summary."
        );
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.error(err);
      setSummary(null);
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err.message ||
          "Unable to fetch combo deal summary."
      );
    } finally {
      if (!controller.signal.aborted) setSummaryLoading(false);
    }
  }, [CompID, buildFilterParams]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useResetPageOnFilterChange(setPageNumber, [
    locationValue,
    viewModeValue,
    rawStartDate,
    rawEndDate,
    committedSearch,
    pageSizeValue,
  ]);

  useDebouncedEffect(() => {
    fetchReport();
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
  }, [fetchReport]);

  const handlePageSizeChange = (option) => {
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "pageSize", value: option }));
    setPageNumber(1);
  };

  const s = summary || {};
  const summaryItems = [
    { label: "Net Sales", value: money(s.totalNetSales ?? s.TotalNetSales) },
    { label: "Qty Sold", value: Number(s.totalQuantitySold ?? s.TotalQuantitySold ?? 0) },
    { label: "Orders", value: Number(s.totalOrders ?? s.TotalOrders ?? 0) },
    { label: "Deals", value: Number(s.packageCount ?? s.PackageCount ?? 0) },
    {
      label: "Deal Net",
      value: money(s.dealNetSales ?? s.DealNetSales),
      hint: `Qty ${Number(s.dealQuantitySold ?? s.DealQuantitySold ?? 0)}`,
    },
    {
      label: "Refunds",
      value: money(s.totalRefundValue ?? s.TotalRefundValue),
      hint: `Qty ${Number(s.totalRefundQty ?? s.TotalRefundQty ?? 0)}`,
    },
  ];

  const listEmpty =
    !loading &&
    ((viewModeValue === "packages" && packages.length === 0) ||
      (viewModeValue === "details" && details.length === 0));

  const renderPackageTable = () => (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0 deal-performance-table">
        <thead>
          <tr>
            <th>Deal Name</th>
            <th>Qty Sold</th>
            <th>Gross Sales</th>
            <th>Discounts</th>
            <th>Net Sales</th>
            <th>Orders</th>
            <th>Refund Qty</th>
            <th>Refund Value</th>
          </tr>
        </thead>
        <tbody>
          {packages.map((row) => (
            <tr key={row.packageID || row.PackageID}>
              <td>{row.packageName || row.PackageName}</td>
              <td>{Number(row.quantitySold ?? row.QuantitySold ?? 0)}</td>
              <td style={{ color: themeColor }}>{money(row.grossSales ?? row.GrossSales)}</td>
              <td>{money(row.discountAmount ?? row.DiscountAmount)}</td>
              <td>{money(row.netSales ?? row.NetSales)}</td>
              <td>{Number(row.orderCount ?? row.OrderCount ?? 0)}</td>
              <td>{Number(row.refundQty ?? row.RefundQty ?? 0)}</td>
              <td>{money(row.refundValue ?? row.RefundValue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderDetailTable = () => (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0 deal-performance-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Order</th>
            <th>Deal</th>
            <th>Qty</th>
            <th>Total</th>
            <th>Status</th>
            <th>Location</th>
            <th>Staff</th>
          </tr>
        </thead>
        <tbody>
          {details.map((row, idx) => (
            <tr key={`${row.orderID || row.OrderID}-${idx}`}>
              <td>
                {moment(row.orderDateTime || row.OrderDateTime).format("DD MMM YYYY HH:mm")}
              </td>
              <td>{row.orderID || row.OrderID}</td>
              <td>{row.packageName || row.PackageName}</td>
              <td>{Number(row.quantity ?? row.Quantity ?? 0)}</td>
              <td style={{ color: themeColor }}>{money(row.totalAmount ?? row.TotalAmount)}</td>
              <td>{row.orderStatus || row.OrderStatus}</td>
              <td>{row.locationName || row.LocationName || "—"}</td>
              <td>{row.staffName || row.StaffName || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="deal-performance-page">
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Combo Deal Performance</li>
          </ol>
          <h1 className="page-header mb-0">Combo Deal Performance</h1>
        </div>
      </div>

      {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

      {initialLoadComplete && (
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title={viewModeValue === "details" ? "Order Lines" : "Combo Deals"}
          totalCount={totalRecords}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search deal name..."
          bodyFlush
          topFilters={
            <>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                <DateRangePicker
                  startDate={
                    rawStartDate
                      ? typeof rawStartDate === "string"
                        ? new Date(rawStartDate)
                        : rawStartDate
                      : null
                  }
                  endDate={
                    rawEndDate
                      ? typeof rawEndDate === "string"
                        ? new Date(rawEndDate)
                        : rawEndDate
                      : null
                  }
                  onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                  allowSingleDate={true}
                  allowDateRange={true}
                  showTimeSelection={true}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-map-marker">
                <Select
                  value={location}
                  onChange={(option) => handleFilterChange("location", option)}
                  options={locations}
                  styles={filterPillSelectStyles}
                  isSearchable={false}
                  isLoading={loadingLocations}
                  placeholder="All Locations"
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-th-large">
                <Select
                  value={viewMode}
                  onChange={(option) => handleFilterChange("viewMode", option)}
                  options={VIEW_MODE_OPTIONS}
                  styles={filterPillSelectStyles}
                  isSearchable={false}
                  placeholder="View"
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
            </>
          }
          headerControls={
            <div className="deal-performance-header-actions d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchSummary}
                disabled={summaryLoading}
                title="Load summary for current filters"
              >
                <i className="fa fa-chart-bar me-1" />
                {summaryLoading ? "Loading..." : "Summary"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchReport}
                title="Refresh"
              >
                <i className="fa fa-refresh me-1" />
                Refresh
              </button>
            </div>
          }
        >
          {summary ? (
            <div className="row g-3 px-3 pt-3 mb-2">
              {summaryItems.map((item) => (
                <div className="col-xl-2 col-lg-4 col-md-4 col-sm-6" key={item.label}>
                  <div
                    className="deal-performance-summary-card"
                    style={{ borderTopColor: themeColor }}
                  >
                    <div className="text-muted text-uppercase small fw-semibold mb-1">
                      {item.label}
                    </div>
                    <div className="fs-5 fw-bold">{item.value}</div>
                    {item.hint ? (
                      <div className="text-muted small mt-1">{item.hint}</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="px-3 pb-3">
            <GridSectionLoader
              loading={loading}
              minHeight="280px"
              message="Fetching combo deal performance..."
            >
              {listEmpty ? (
                <GridEmptyState
                  isDarkMode={isDarkMode}
                  themeColor={themeColor}
                  title="No combo deal sales found"
                  description="Try adjusting your filters or date range."
                  onClearFilters={hasFiltersApplied() ? clearAllFilters : undefined}
                  clearFiltersLabel="Clear all filters"
                />
              ) : (
                <>
                  {viewModeValue === "packages" ? renderPackageTable() : renderDetailTable()}
                  <ModernGridPagerFooter
                    pageSize={pageSizePref}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      )}
    </div>
  );
};

export default DealPerformanceReport;
